import { z } from 'zod';
import { MoneySchema, SlugSchema, TimestampSchema, UidSchema } from './primitives.js';

/**
 * Sprint 120 — Settlement-statement cross-check report.
 *
 * Stored at `platformBrands/{brandId}/settlementReports/{id}` where the
 * id is `ts-{uploadMs}` (string-sortable, newest first when listed desc).
 *
 * Where the daily reconciliation report ([[reconciliation.ts]]) verifies
 * "every paid intent landed a wallet credit", THIS report verifies
 * "every paid intent appears in the PSP's own settlement statement" —
 * i.e. toyyibPay's own bookkeeping agrees with ours. Together the two
 * reports close the audit loop:
 *
 *   intent  →  wallet credit  →  PSP settlement
 *   └ Sprint 100 covers this ┘
 *                  └─── Sprint 120 covers this ───┘
 *
 * Created by the super-admin uploading a CSV exported from toyyibPay's
 * dashboard (Sales Report or per-bill export). The helper parses the
 * CSV, indexes rows by their `billExternalReferenceNo` (we set this to
 * `{tenantId}__{intentId}` at createBill time — [[createToyyibPayBill]]),
 * pulls our paid intents for the date range the CSV spans, and emits a
 * `clean === true` doc when every row resolves to a paid intent of the
 * same amount AND every paid intent in the range appears in the CSV.
 */

export const SettlementSourceSchema = z.enum([
  'toyyibpay',
  // Future: 'bank_statement' once Kilat ingests the merchant's bank CSV directly.
]);
export type SettlementSource = z.infer<typeof SettlementSourceSchema>;

export const SettlementMismatchReasonSchema = z.enum([
  /** CSV row was structurally invalid (missing required cols, bad amount, etc.). */
  'unparseable_row',
  /** CSV row's externalRef doesn't match any payment intent we have. Could be
   *  a forged dashboard export, a stray bill created outside Kilat's flow,
   *  or a CSV from a totally different PSP account. */
  'unknown_external_ref',
  /** CSV row is paid but our matching intent isn't in `paid` status.
   *  Our webhook failed to flip it — operator wasn't credited even though
   *  the customer paid AND toyyibPay successfully captured the funds. */
  'intent_not_paid',
  /** CSV amount differs from `intent.totalChargedSen`. Should be exact —
   *  the customer pays totalChargedSen at the PSP, and the PSP records
   *  that exact amount in its statement. */
  'amount_mismatch',
  /** We have a paid intent in the CSV's date range, but no row in the
   *  CSV references it. Most-serious mismatch: webhook flipped intent
   *  to paid but the PSP doesn't actually have the money. Could mean a
   *  forged webhook (no HMAC on toyyibPay's side), a partial export
   *  (super-admin uploaded only one day of a multi-day window), or a
   *  PSP-side cancellation that didn't fire a webhook. */
  'paid_intent_not_in_settlement',
]);
export type SettlementMismatchReason = z.infer<typeof SettlementMismatchReasonSchema>;

export const SettlementMismatchSchema = z.object({
  reason: SettlementMismatchReasonSchema,
  /** Source CSV row index (0-based, after the header row). Omitted for
   *  `paid_intent_not_in_settlement` (no row, only an intent). */
  rowIndex: z.number().int().nonnegative().optional(),
  /** The external reference recorded on the CSV row (or derived from the
   *  matched intent if missing CSV). `{tenantId}__{intentId}` shape. */
  externalRef: z.string().optional(),
  tenantId: z.string().optional(),
  intentId: z.string().optional(),
  /** Free-form context for super-admin review. */
  detail: z.string().max(500),
  /** Amount the CSV row reported, when applicable. */
  csvAmountSen: MoneySchema.optional(),
  /** Amount we expected (intent.totalChargedSen), when applicable. */
  intentAmountSen: MoneySchema.optional(),
});
export type SettlementMismatch = z.infer<typeof SettlementMismatchSchema>;

export const SettlementReportSchema = z.object({
  /** `ts-{uploadMs}` — string-sortable, newest first when ordered desc. */
  id: z.string().min(1),
  brandId: SlugSchema,
  source: SettlementSourceSchema,
  // ── CSV metadata ───────────────────────────────────────────────────
  fileName: z.string().max(255),
  fileSizeBytes: z.number().int().nonnegative(),
  /** Raw header row from the CSV (after normalisation). Helps when
   *  toyyibPay changes their export format — super-admin can see what
   *  columns we actually parsed. */
  detectedHeaders: z.array(z.string()),
  // ── Date range covered ─────────────────────────────────────────────
  /** Earliest paid-row timestamp in the CSV. Null if CSV had no parseable
   *  paid rows (rare; only on totally malformed uploads). */
  periodStart: TimestampSchema.optional(),
  /** Latest paid-row timestamp in the CSV. */
  periodEnd: TimestampSchema.optional(),
  // ── Counts ──────────────────────────────────────────────────────────
  /** Total rows in the CSV (excluding header). */
  rowsTotal: z.number().int().nonnegative(),
  /** Rows that parsed cleanly. */
  rowsParsed: z.number().int().nonnegative(),
  /** Rows whose externalRef matched a paid intent at the matching amount. */
  rowsMatched: z.number().int().nonnegative(),
  /** Paid intents in [periodStart, periodEnd] that the CSV did NOT cover. */
  intentsMissingFromCsv: z.number().int().nonnegative(),
  // ── Money ──────────────────────────────────────────────────────────
  /** Sum of `amountSen` from all PARSED rows the CSV reports as paid. */
  totalCsvAmountSen: MoneySchema,
  /** Sum of `intent.totalChargedSen` across intents we matched against
   *  the CSV (so super-admin can spot drift at a glance). */
  totalMatchedIntentSen: MoneySchema,
  // ── Discrepancies ──────────────────────────────────────────────────
  mismatches: z.array(SettlementMismatchSchema),
  /** Convenience: true when zero mismatches. */
  clean: z.boolean(),
  // ── Metadata ───────────────────────────────────────────────────────
  uploadedAt: TimestampSchema,
  uploadedBy: UidSchema,
});
export type SettlementReport = z.infer<typeof SettlementReportSchema>;
